import React, { useState } from 'react';
import { Button } from './ui/button';
import { Settings, BarChart3, ShieldCheck, UserCheck, CheckCircle2, Package,
         GraduationCap, IndianRupee, Wrench, Target } from 'lucide-react';

/**
 * An icon and a gradient for a KPI, chosen from what it MEASURES.
 *
 * Matched on the KPI's own words rather than on a stored category, because
 * `kpi_master` has no category field — inventing one on screen would imply a
 * grouping the data does not carry. The fallback is deliberate and plain, so a
 * KPI that matches nothing still looks like the others.
 *
 * Gradients come from the app's own tokens (`--g-*` in index.css), so this
 * cannot drift away from the rest of the product.
 */
const KPI_LOOKS = [
  // [matcher, icon, gradient token, tint, ink-on-tint]
  [/\bpm\b|preventive|maintenance|breakdown|mtbf|mttr|tool/i,
   Wrench, 'var(--g-kpi-dept)', '#F3EEFF', '#7C3AED'],
  [/efficien|production|output|stroke|manpower|productiv/i,
   BarChart3, 'var(--g-data)', '#EAF2FF', '#2563EB'],
  [/safety|accident|incident|near miss/i,
   ShieldCheck, 'var(--g-alert)', '#FFEDED', '#DC2626'],
  [/late|attendance|absent|induction|onboard|portal/i,
   UserCheck, 'var(--g-kpi-people)', '#EAF2FF', '#2563EB'],
  [/quality|rejection|ppm|rework|red tag|complaint|inspect/i,
   CheckCircle2, 'var(--g-success)', '#E8F7EE', '#059669'],
  [/hold material|inventory|stock|store|dispatch|supply|schedule/i,
   Package, 'var(--g-warning)', '#FFF4E3', '#D97706'],
  [/training|kaizen|suggestion|skill/i,
   GraduationCap, 'var(--g-celebrate)', '#F3EEFF', '#6D28D9'],
  [/cost|expense|salary|sales|budget|value|₹/i,
   IndianRupee, 'var(--g-kpi-salary)', '#FFEFF4', '#E11D74'],
  [/attrition|recruit|joiner/i,
   Settings, 'var(--g-ai)', '#EEF0FF', '#4338CA'],
];

export function kpiLook(kpi) {
  const hay = `${kpi?.name || ''} ${kpi?.department || ''} ${kpi?.key || ''}`;
  for (const [re, Icon, grad, tint, ink] of KPI_LOOKS) {
    if (re.test(hay)) return { Icon, grad, tint, ink };
  }
  return { Icon: Target, grad: 'var(--g-primary)', tint: '#EEF3FF', ink: '#4F46E5' };
}

/** The icon tile. `solid` fills it with the gradient; otherwise a soft tint. */
export function KpiIcon({ kpi, size = 36, solid = false, className = '' }) {
  const { Icon, grad, tint, ink } = kpiLook(kpi);
  return (
    <span
      className={`inline-flex items-center justify-center rounded-xl shrink-0 kpi-icon ${className}`}
      style={{
        width: size, height: size,
        background: solid ? grad : tint,
        boxShadow: solid ? '0 6px 16px -4px rgba(79,70,229,0.45)' : 'none',
      }}>
      <Icon size={Math.round(size * 0.5)} strokeWidth={2}
        style={{ color: solid ? '#FFFFFF' : ink }} />
    </span>
  );
}

/**
 * One KPI, read-only, in the right-hand pane of KPI Master.
 *
 * The screen it replaced put eight columns on every one of 229 rows — including
 * the dotted source path and the internal key — so choosing a KPI meant reading
 * past the two things that never help you choose. Here the list answers "which
 * KPI", and this answers "what is it, and how is it scored".
 *
 * Technical facts are not removed, only collapsed: the key still binds
 * templates and marks, and somebody debugging a mapping still needs the path.
 * They live under Advanced, one click away, together with the per-plant targets
 * that used to occupy the top of the page for the sake of two KPIs.
 *
 * Nothing here computes anything. Every value is handed in.
 */

function Field({ label, children, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[#98A1B2] mb-0.5">{label}</div>
      <div className={`text-[13px] text-[#11182B] ${mono ? 'font-mono text-[12px] break-all' : ''}`}>
        {children}
      </div>
    </div>
  );
}

export default function KpiMasterPanel({ kpi, group, roles, onEdit, onEditLadder,
                                         onDelete, unitTargets, bandsText }) {
  const [advanced, setAdvanced] = useState(false);

  if (!kpi) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-[#98A1B2] p-8 text-center"
        data-testid="kpi-panel-empty">
        Choose a KPI on the left to see what it measures and how it is scored.
      </div>
    );
  }

  const auto = kpi.mode !== 'manual';
  // `responsible` is hr | hod — WHO FILLS a manual KPI, not a named owner. The
  // label says exactly that rather than implying ownership the data has not got.
  const filledBy = auto ? 'The software' :
    (kpi.responsible === 'hod' ? 'Reporting head' : 'HR');
  const source = kpi.field
    ? (group?.label || 'Connected software')
    : (group?.source === 'manual' ? 'Typed in each month' : 'Not mapped yet');

  return (
    // `key` on the KPI so switching selection replays the entrance — the panel
    // changing is the thing worth noticing, and without it the swap is silent.
    <div data-testid="kpi-panel" key={kpi.key} className="enter-up">
      {/* The header carries the KPI's own gradient as a thin wash rather than a
          block of colour: enough to tell two KPIs apart at a glance, not enough
          to compete with the figures underneath. */}
      <div className="flex items-start gap-3 px-5 py-4 border-b border-[#F2F4F8] relative overflow-hidden">
        <span aria-hidden className="absolute inset-x-0 top-0 h-[3px]"
          style={{ background: kpiLook(kpi).grad }} />
        <KpiIcon kpi={kpi} size={40} solid className="enter-pop" />
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-[#172554] text-[15px] leading-snug">{kpi.name}</h3>
          <p className="text-[12px] text-[#5A6377] mt-0.5">
            {kpi.department || 'No department'}
            {kpi.uom ? ` · ${kpi.uom}` : ''}
            {' · '}
            <span className={auto ? 'text-[#2563EB] font-medium' : 'text-[#8A5A12] font-medium'}>
              {auto ? 'Automatic' : 'Typed in'}
            </span>
            {kpi.active === false && <span className="text-[#C2151F] font-medium"> · Retired</span>}
          </p>
        </div>
        <Button size="sm" onClick={onEdit} data-testid="kpi-panel-edit">Edit</Button>
      </div>

      <div className="px-5 py-4 space-y-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Target">{kpi.target ?? '—'}</Field>
          <Field label="Filled by">{filledBy}</Field>
          <Field label="Direction">
            {kpi.direction === 'lower' ? 'Lower is better' : 'Higher is better'}
          </Field>
          <Field label="Data source">{source}</Field>
        </div>

        {kpi.objective && <Field label="Objective">{kpi.objective}</Field>}

        {/* The ladder, read as a table rather than as the one-line sentence the
            old screen squeezed into a column. */}
        <div className="rounded-lg border border-[#E6E9F0] overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-[#FAFBFD] border-b border-[#E6E9F0]">
            <span className="text-[10px] uppercase tracking-wide text-[#98A1B2]">Marking ladder</span>
            {/* Said on the ladder itself, not in a footnote. A provisional
                ladder scores exactly like any other, so the only thing marking
                it out is this — and somebody reading a mark deserves to know
                the thresholds were derived from a convention rather than agreed
                by management. */}
            {kpi.bands_provisional && (
              <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-[#FFF4E3] text-[#B14A0E]"
                data-testid="kpi-panel-provisional" title="Thresholds not yet agreed by management — edit them here">
                PROVISIONAL
              </span>
            )}
            <Button size="sm" variant="outline" className="ml-auto h-7 text-xs"
              onClick={onEditLadder} data-testid="kpi-panel-edit-ladder">Edit ladder</Button>
          </div>
          {(kpi.bands || []).length ? (
            <table className="w-full text-[13px]">
              <tbody>
                {bandsText.map((row, i) => (
                  <tr key={i} className="border-b border-[#F2F4F8] last:border-0">
                    <td className="px-3 py-1.5 text-[#5A6377]">{row.condition}</td>
                    <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-[#11182B] w-24">
                      {row.score} {Math.abs(row.score) === 1 ? 'mark' : 'marks'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="px-3 py-3 text-[13px] text-[#B14A0E]" data-testid="kpi-panel-no-ladder">
              No marking rule set — this KPI cannot be scored until one is added.
            </p>
          )}
          {/* Plants that are marked against a ladder of their OWN, named here
              because the table above is only the SHARED one. Without this the
              read-only view shows a ladder those plants are not scored by, and
              a plant-specific save looks like it did nothing. */}
          {!!Object.keys(kpi.bands_by_unit || {}).length && (
            <p className="px-3 py-2 text-[11px] text-[#1E3A8A] border-t border-[#F2F4F8] bg-[#F5F8FF]"
              data-testid="kpi-panel-plant-ladders">
              {Object.keys(kpi.bands_by_unit).length} plant(s) are marked against their own
              ladder, not the one above:{' '}
              {Object.keys(kpi.bands_by_unit)
                .map((u) => String(u).replace(/．/g, '.').replace(/＄/g, '$'))
                .join(', ')}. Open <span className="font-medium">Edit ladder</span> and pick
              the plant to see it.
            </p>
          )}
          {/* Where the thresholds came from, in the KPI's own words. Without it
              a provisional ladder is indistinguishable from an agreed one the
              moment anybody stops reading the badge. */}
          {kpi.bands_why && (
            <p className="px-3 py-2 text-[11px] text-[#5A6377] border-t border-[#F2F4F8] bg-[#FAFBFD]"
              data-testid="kpi-panel-bands-why">{kpi.bands_why}</p>
          )}
        </div>

        {/* Everything technical, collapsed. Not removed: the key is what binds
            templates and marks to this KPI, and the path is what a mapping
            problem is diagnosed from. They simply do not belong on a list you
            are scanning to find a KPI. */}
        <div className="rounded-lg border border-[#E6E9F0]">
          <button onClick={() => setAdvanced((v) => !v)} data-testid="kpi-panel-advanced"
            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#FAFBFD]">
            <span className={`text-[#98A1B2] text-[10px] transition-transform ${advanced ? 'rotate-90' : ''}`}>▶</span>
            <span className="text-[12px] font-medium text-[#5A6377]">Advanced</span>
            <span className="text-[11px] text-[#C9CFDC] ml-auto">
              key, data mapping{unitTargets ? ', plant targets' : ''}
            </span>
          </button>
          {advanced && (
            <div className="px-3 pb-3 pt-1 space-y-3 border-t border-[#F2F4F8]">
              <Field label="KPI key" mono>{kpi.key}</Field>
              <Field label="Reads" mono>
                {kpi.field || <span className="font-sans text-[#98A1B2]">nothing mapped</span>}
              </Field>
              {kpi.denominator_field && (
                <Field label="Divided by" mono>{kpi.denominator_field}</Field>
              )}
              <Field label="Owned by">{group?.label || '—'}</Field>
              <Field label={`Used by ${(roles || []).length} role(s)`}>
                {(roles || []).length
                  ? <div className="max-h-28 overflow-y-auto text-[#5A6377] text-[12px]">
                      {roles.map((r) => <div key={r}>{r}</div>)}
                    </div>
                  : <span className="text-[#C2151F] text-[12px]">not on any role sheet</span>}
              </Field>
              {/* Per-plant targets. Unchanged behaviour — this is the same
                  component that used to sit above the KPI list, where it took
                  a third of the page for the sake of two KPIs. */}
              {unitTargets && (
                <div className="pt-1">
                  <div className="text-[10px] uppercase tracking-wide text-[#98A1B2] mb-1">
                    Targets per plant
                  </div>
                  {unitTargets}
                </div>
              )}
              {/* Deleting a KPI reaches templates, sheets and marks, so it sits
                  here rather than as an ✕ on a list row where it was one
                  mis-click from the row you meant to open. The confirmation
                  that follows reports exactly what would be removed. */}
              {onDelete && (
                <div className="pt-2 border-t border-[#F2F4F8]">
                  <button onClick={onDelete} data-testid="kpi-panel-delete"
                    className="text-[12px] text-[#C2151F] hover:underline">
                    Delete this KPI…
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
